import { useState, useEffect } from 'react';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { storage } from '../lib/firebase';

interface StorageFile {
  name: string;
  downloadUrl: string;
  contentType: string;
  size: number;
  timeCreated: string;
  fullPath: string;
}

interface UseStorageFilesResult {
  extensions: StorageFile[];
  thumbnails: StorageFile[];
  loading: boolean;
  error: string | null;
  refreshFiles: () => Promise<void>;
}

export function useStorageFiles(): UseStorageFilesResult {
  const [extensions, setExtensions] = useState<StorageFile[]>([]);
  const [thumbnails, setThumbnails] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get references to the extensions and thumbnails folders
      const extensionsRef = ref(storage, 'extensions');
      const thumbnailsRef = ref(storage, 'thumbnails');

      // List all files in both folders
      const [extensionsResult, thumbnailsResult] = await Promise.all([
        listAll(extensionsRef),
        listAll(thumbnailsRef)
      ]);

      // Fetch details for extension files
      const extensionFiles = await Promise.all(
        extensionsResult.items.map(async (item) => {
          try {
            const [downloadUrl, metadata] = await Promise.all([
              getDownloadURL(item),
              getMetadata(item)
            ]);

            return {
              name: item.name,
              downloadUrl,
              contentType: metadata.contentType || 'application/javascript',
              size: metadata.size,
              timeCreated: metadata.timeCreated,
              fullPath: metadata.fullPath
            };
          } catch {
            return null;
          }
        })
      );

      // Fetch details for thumbnail files
      const thumbnailFiles = await Promise.all(
        thumbnailsResult.items.map(async (item) => {
          try {
            const [downloadUrl, metadata] = await Promise.all([
              getDownloadURL(item),
              getMetadata(item)
            ]);

            return {
              name: item.name,
              downloadUrl,
              contentType: metadata.contentType || 'image/png',
              size: metadata.size,
              timeCreated: metadata.timeCreated,
              fullPath: metadata.fullPath
            };
          } catch {
            return null;
          }
        })
      );

      // Filter out any null values from failed fetches
      const validExtensions = extensionFiles.filter((file): file is StorageFile => file !== null);
      const validThumbnails = thumbnailFiles.filter((file): file is StorageFile => file !== null);

      setExtensions(validExtensions);
      setThumbnails(validThumbnails);
    } catch {
      setError('Failed to fetch files from storage');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchFiles();
  }, []);

  return {
    extensions,
    thumbnails,
    loading,
    error,
    refreshFiles: fetchFiles
  };
} 